import {
  type ComponentRef,
  type PropsWithChildren,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import {
  Keyboard,
  KeyboardAvoidingView,
  Dimensions,
  Platform,
  ScrollView,
  TextInput,
  View,
  type ScrollViewProps,
  type ViewProps,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { SPACING } from '../../lib/brand'
import { useProductTelemetryScroll } from '../../lib/hooks/use-product-telemetry-scroll'

interface KeyboardAwareViewProps extends Omit<
  ScrollViewProps,
  'children' | 'contentContainerStyle'
> {
  bottomOffset?: number
  enableOnAndroid?: boolean
  /**
   * Auth screens render outside ScreenContainer, so they need the safe-area
   * top inset here. Screens already wrapped by ScreenContainer pass false to
   * avoid double-counting the status bar/notch.
   */
  includeTopInset?: boolean
  scrollRef?: RefObject<ComponentRef<typeof ScrollView> | null>
  /** Offset from the top of the native window to this view (for fixed headers). */
  keyboardVerticalOffset?: number
  contentContainerStyle?: ScrollViewProps['contentContainerStyle']
  onFocus?: ViewProps['onFocus']
}

export function KeyboardAwareView({
  children,
  bottomOffset = 24,
  bounces = true,
  enableOnAndroid = true,
  includeTopInset = true,
  scrollRef,
  keyboardVerticalOffset = 0,
  contentContainerStyle,
  onFocus,
  onScroll,
  keyboardDismissMode,
  keyboardShouldPersistTaps,
  automaticallyAdjustKeyboardInsets,
  showsVerticalScrollIndicator,
  scrollEventThrottle,
  ...scrollViewProps
}: PropsWithChildren<KeyboardAwareViewProps>) {
  const insets = useSafeAreaInsets()
  const handleTelemetryScroll = useProductTelemetryScroll('keyboard_aware.scroll')
  const internalScrollRef = useRef<ComponentRef<typeof ScrollView> | null>(null)
  const pendingFocusScrollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const keyboardHeightRef = useRef(0)
  const scrollOffsetRef = useRef(0)
  const bottomInset =
    Platform.OS === 'android' ? Math.max(insets.bottom, SPACING['2xl']) : insets.bottom
  const topInset = includeTopInset ? insets.top : 0

  const keyboardBehavior =
    Platform.OS === 'ios' ? 'padding' : enableOnAndroid ? 'height' : undefined

  const assignScrollRef = useCallback(
    (node: ComponentRef<typeof ScrollView> | null) => {
      internalScrollRef.current = node
      if (scrollRef) scrollRef.current = node
    },
    [scrollRef],
  )

  const scrollFocusedInputIntoView = useCallback(() => {
    if (Platform.OS === 'web') return
    const focusedInput = TextInput.State.currentlyFocusedInput()
    if (!focusedInput) return
    const scrollView = internalScrollRef.current
    if (!scrollView) return

    // Keep the platform-native path as the first attempt. The measured delta
    // below is a Fabric-safe fallback for devices where this helper reports
    // success without actually moving the scroll view.
    scrollView.scrollResponderScrollNativeHandleToKeyboard(focusedInput, bottomOffset, true)

    // Fabric's scrollResponder helper can report success without moving the
    // ScrollView. Measure the focused field against the actual window and move
    // only the obscured delta; this works consistently on iOS and Android,
    // including edge-to-edge layouts and custom wizard headers.
    focusedInput.measureInWindow((_x, y, _width, height) => {
      const keyboardTop = Dimensions.get('window').height - keyboardHeightRef.current
      const visibleBottom = keyboardTop - bottomOffset
      const overlap = y + height - visibleBottom
      if (overlap > 0) {
        scrollView.scrollTo({ y: Math.max(0, scrollOffsetRef.current + overlap), animated: true })
      }
    })
  }, [bottomOffset])

  const scheduleFocusedInputScroll = useCallback(
    (delayMs: number) => {
      if (pendingFocusScrollRef.current) clearTimeout(pendingFocusScrollRef.current)
      pendingFocusScrollRef.current = setTimeout(() => {
        pendingFocusScrollRef.current = null
        scrollFocusedInputIntoView()
      }, delayMs)
    },
    [scrollFocusedInputIntoView],
  )

  useEffect(() => {
    if (Platform.OS === 'web') return

    // `keyboardDidShow` runs after Android edge-to-edge and iOS have reported
    // the final keyboard frame. Measuring sooner races the resize and scrolls
    // to yesterday's viewport, leaving the bottom half of the field covered.
    const subscription = Keyboard.addListener('keyboardDidShow', (event) => {
      keyboardHeightRef.current = event.endCoordinates?.height ?? 0
      scheduleFocusedInputScroll(120)
    })
    const frameSubscription = Keyboard.addListener('keyboardWillChangeFrame', (event) => {
      keyboardHeightRef.current = event.endCoordinates?.height ?? 0
    })
    const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
      keyboardHeightRef.current = 0
    })

    return () => {
      subscription.remove()
      frameSubscription.remove()
      hideSubscription.remove()
      if (pendingFocusScrollRef.current) clearTimeout(pendingFocusScrollRef.current)
    }
  }, [scheduleFocusedInputScroll])

  const handleFocus: NonNullable<ViewProps['onFocus']> = (event) => {
    onFocus?.(event)
    // Also run when the keyboard is already open and the user moves from one
    // field to another; in that case no new keyboardDidShow event is emitted.
    scheduleFocusedInputScroll(Platform.OS === 'android' ? 120 : 100)
  }

  const handleScroll: NonNullable<ScrollViewProps['onScroll']> = (event) => {
    scrollOffsetRef.current = event.nativeEvent.contentOffset.y
    handleTelemetryScroll(event)
    onScroll?.(event)
  }

  return (
    <KeyboardAvoidingView
      behavior={keyboardBehavior}
      keyboardVerticalOffset={keyboardVerticalOffset}
      enabled={Platform.OS !== 'web' && (Platform.OS === 'ios' || enableOnAndroid)}
      style={{ flex: 1, width: '100%', alignSelf: 'stretch' }}
    >
      <View style={{ flex: 1, width: '100%' }} onFocus={handleFocus}>
        <ScrollView
          {...scrollViewProps}
          ref={assignScrollRef}
          bounces={bounces}
          onScroll={handleScroll}
          scrollEventThrottle={scrollEventThrottle ?? 250}
          contentContainerStyle={[
            {
              flexGrow: 1,
              paddingHorizontal: SPACING.lg,
              paddingTop: topInset + SPACING.md,
              paddingBottom: bottomInset + bottomOffset,
            },
            contentContainerStyle,
          ]}
          automaticallyAdjustKeyboardInsets={
            automaticallyAdjustKeyboardInsets ?? Platform.OS === 'ios'
          }
          keyboardShouldPersistTaps={keyboardShouldPersistTaps ?? 'handled'}
          keyboardDismissMode={
            keyboardDismissMode ?? (Platform.OS === 'ios' ? 'interactive' : 'on-drag')
          }
          showsVerticalScrollIndicator={showsVerticalScrollIndicator ?? false}
        >
          {children}
        </ScrollView>
      </View>
    </KeyboardAvoidingView>
  )
}
